import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Plus, X, Trash2, Pencil, ChevronUp, ChevronDown, GripVertical, UserPlus,
  Layers, Flag, KanbanSquare, CalendarRange, DollarSign, Wallet, Loader2, MessageSquare, Paperclip, Download, Send, GitCompare, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import { useI18n } from "../../context/I18nContext";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";
const COL_KEYS = ["todo", "in_progress", "review", "done"];
const PRIO = { low: "bg-navy/10 text-navy/60", medium: "bg-gold/15 text-gold", high: "bg-rose-500/10 text-rose-600" };
const STAGE_CLS = { pending: "bg-navy/5 text-navy/50", in_progress: "bg-violet/10 text-violet", done: "bg-emerald-500/10 text-emerald-600" };
const FIN_KEYS = ["expense", "supplier_payment", "supplier_quote"];
const TAB_DEFS = [
  ["resumen", Layers], ["etapas", Flag], ["tablero", KanbanSquare],
  ["cronograma", CalendarRange], ["cotizacion", DollarSign], ["finanzas", Wallet],
  ["comentarios", MessageSquare],
];

async function downloadBlob(url, filename, errMsg) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = href; a.download = filename || "archivo"; a.click();
    URL.revokeObjectURL(href);
  } catch (e) { toast.error(errMsg || "Error"); }
}

export default function OperacionProject() {
  const { id } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("resumen");
  const [invite, setInvite] = useState(false);

  const load = useCallback(() => api.get(`/operations/projects/${id}`).then((r) => setData(r.data))
    .catch(() => toast.error(t("os.ops.loadError"))), [id, t]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <OSLayout title={t("os.ops.projectInternal")}><div className="py-20 text-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div></OSLayout>;
  const p = data.project;

  return (
    <OSLayout title={p.name}>
      <div className="space-y-6 max-w-5xl">
        <Link to="/os/operaciones" data-testid="ops-back" className="inline-flex items-center gap-1.5 text-sm text-navy/50 hover:text-navy transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t("os.ops.title")}
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="overline text-navy/40 mb-1">{p.area || t("os.ops.projectInternal")}</div>
            <h1 className="font-editorial text-3xl md:text-4xl tracking-tight" data-testid="ops-title">{p.name}</h1>
            {p.description && <p className="text-navy/60 mt-2 max-w-2xl">{p.description}</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {(data.members || []).slice(0, 5).map((m) => (
                <div key={m.id} title={m.name} className="w-8 h-8 rounded-full bg-violet/15 text-violet text-xs flex items-center justify-center border-2 border-cream font-medium">
                  {(m.name || "?").slice(0, 1).toUpperCase()}
                </div>
              ))}
            </div>
            <button data-testid="ops-invite" onClick={() => setInvite(true)}
              className="inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2.5 hover:border-violet hover:text-violet transition-colors">
              <UserPlus className="w-4 h-4" /> {t("os.ops.invite")}
            </button>
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-navy/10 pb-px">
          {TAB_DEFS.map(([k, Icon]) => (
            <button key={k} data-testid={`ops-tab-${k}`} onClick={() => setTab(k)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-violet text-violet" : "border-transparent text-navy/50 hover:text-navy"}`}>
              <Icon className="w-4 h-4" /> {t(`os.ops.tabs.${k}`)}
            </button>
          ))}
        </div>

        <div data-testid={`ops-panel-${tab}`}>
          {tab === "resumen" && <Resumen p={p} data={data} />}
          {tab === "etapas" && <Stages id={id} p={p} onChange={load} />}
          {tab === "tablero" && <Board id={id} data={data} onChange={load} />}
          {tab === "cronograma" && <Timeline data={data} />}
          {tab === "cotizacion" && <Quote id={id} p={p} onSaved={load} />}
          {tab === "finanzas" && <Finance id={id} data={data} onChange={load} />}
          {tab === "comentarios" && <Comments id={id} />}
        </div>
      </div>

      {invite && <ShareDialog collection="ops_projects" id={id} title={p.name} onClose={() => { setInvite(false); load(); }} />}
    </OSLayout>
  );
}

/* ---------------- Resumen ---------------- */
function Resumen({ p, data }) {
  const { t } = useI18n();
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white border border-navy/10 rounded-lg p-6 space-y-4">
        <div className="overline text-navy/40">{t("os.ops.stages")}</div>
        <div className="space-y-2">
          {(p.stages || []).map((s) => (
            <div key={s.key} className="flex items-center justify-between">
              <span className="text-sm">{s.label}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${STAGE_CLS[s.status]}`}>{t(`os.ops.stageStatus.${s.status}`)}</span>
            </div>
          ))}
          {(p.stages || []).length === 0 && <div className="text-navy/40 text-sm">{t("os.ops.noStages")}</div>}
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-navy/10 rounded-lg p-6">
          <div className="overline text-navy/40 mb-2">{t("os.ops.stagesProgress")}</div>
          <div className="text-3xl font-medium">{p.progress || 0}%</div>
          <div className="mt-3 h-2 bg-navy/10 rounded-full overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${p.progress || 0}%` }} /></div>
        </div>
        <div className="bg-white border border-navy/10 rounded-lg p-6 grid grid-cols-2 gap-3 text-center">
          <div><div className="text-2xl font-medium">{data.tasks.length}</div><div className="text-xs text-navy/40">{t("os.ops.tasks")}</div></div>
          <div><div className="text-2xl font-medium text-rose-600">{money(data.totals.expenses)}</div><div className="text-xs text-navy/40">{t("os.ops.expenseTotal")}</div></div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Stages ---------------- */
function Stages({ id, p, onChange }) {
  const { t } = useI18n();
  const [edit, setEdit] = useState(false);
  const [stages, setStages] = useState(p.stages || []);
  useEffect(() => { setStages(p.stages || []); }, [p.stages]);

  const toggle = async (key, status) => {
    try { const { data: r } = await api.patch(`/operations/projects/${id}/stages/${key}`, { status }); setStages(r.stages); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.ops.err")); }
  };
  const save = async () => {
    try { await api.put(`/operations/projects/${id}/stages`, { stages }); setEdit(false); toast.success(t("os.ops.stagesUpdated")); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.ops.err")); }
  };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= stages.length) return; const a = [...stages]; [a[i], a[j]] = [a[j], a[i]]; setStages(a); };

  return (
    <div className="bg-white border border-navy/10 rounded-lg p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="overline text-navy/40">{t("os.ops.projectStages")}</div>
        {edit ? (
          <div className="flex items-center gap-2">
            <button data-testid="ops-stages-add" onClick={() => setStages([...stages, { key: `new-${Date.now()}`, label: "", status: "pending" }])} className="inline-flex items-center gap-1.5 text-sm text-navy/60 hover:text-navy"><Plus className="w-4 h-4" /> {t("os.ops.add")}</button>
            <button data-testid="ops-stages-save" onClick={save} className="rounded-full bg-gradient-violet text-white text-sm px-4 py-2 hover:opacity-90">{t("os.ops.save")}</button>
            <button onClick={() => { setEdit(false); setStages(p.stages || []); }} className="text-navy/40 hover:text-navy"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <button data-testid="ops-stages-edit" onClick={() => setEdit(true)} className="inline-flex items-center gap-1.5 text-sm text-navy/60 hover:text-navy"><Pencil className="w-3.5 h-3.5" /> {t("os.ops.editStages")}</button>
        )}
      </div>
      {edit ? (
        <div className="space-y-2">
          {stages.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-navy/20" />
              <input data-testid={`ops-stage-input-${i}`} value={s.label} placeholder={t("os.ops.stageNamePlaceholder")}
                onChange={(e) => { const a = [...stages]; a[i] = { ...a[i], label: e.target.value }; setStages(a); }} className={input} />
              <select value={s.status} onChange={(e) => { const a = [...stages]; a[i] = { ...a[i], status: e.target.value }; setStages(a); }}
                className="bg-cream border border-navy/10 rounded-lg px-2 py-2 text-sm outline-none focus:border-violet">
                <option value="pending">{t("os.ops.stageStatus.pending")}</option><option value="in_progress">{t("os.ops.stageStatus.in_progress")}</option><option value="done">{t("os.ops.stageStatus.done")}</option>
              </select>
              <button onClick={() => move(i, -1)} className="text-navy/30 hover:text-navy"><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} className="text-navy/30 hover:text-navy"><ChevronDown className="w-4 h-4" /></button>
              <button data-testid={`ops-stage-remove-${i}`} onClick={() => setStages(stages.filter((_, k) => k !== i))} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {stages.map((s) => (
            <div key={s.key} data-testid={`ops-stage-${s.key}`} className="border border-navy/10 rounded-lg p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{s.label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STAGE_CLS[s.status]}`}>{t(`os.ops.stageStatus.${s.status}`)}</span>
              </div>
              <div className="mt-3 flex gap-1.5">
                {["pending", "in_progress", "done"].map((st) => (
                  <button key={st} onClick={() => toggle(s.key, st)}
                    className={`flex-1 text-[10px] py-1.5 rounded transition-colors ${s.status === st ? "bg-navy text-cream" : "bg-navy/5 text-navy/50 hover:bg-navy/10"}`}>{t(`os.ops.stageStatus.${st}`)}</button>
                ))}
              </div>
            </div>
          ))}
          {stages.length === 0 && <div className="text-navy/40 text-sm">{t("os.ops.noStagesEdit")}</div>}
        </div>
      )}
    </div>
  );
}

/* ---------------- Board ---------------- */
function Board({ id, data, onChange }) {
  const { t } = useI18n();
  const [adding, setAdding] = useState("");
  const [title, setTitle] = useState("");
  const members = data.members || [];
  const add = async (status) => {
    if (!title.trim()) return;
    try { await api.post(`/operations/projects/${id}/tasks`, { title: title.trim(), status }); setTitle(""); setAdding(""); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.ops.err")); }
  };
  const upd = async (tk, body) => { try { await api.patch(`/operations/tasks/${tk.id}`, body); onChange(); } catch (e) { toast.error(t("os.ops.err")); } };
  const del = async (tk) => { try { await api.delete(`/operations/tasks/${tk.id}`); onChange(); } catch (e) { toast.error(t("os.ops.err")); } };
  return (
    <div className="grid md:grid-cols-4 gap-3">
      {COL_KEYS.map((st) => {
        const items = data.tasks.filter((tk) => tk.status === st);
        return (
          <div key={st} data-testid={`ops-board-col-${st}`} className="bg-navy/[0.03] rounded-lg p-3 min-h-[120px]">
            <div className="flex items-center justify-between mb-2"><span className="text-xs font-medium text-navy/60">{t(`os.ops.cols.${st}`)}</span><span className="text-xs text-navy/30">{items.length}</span></div>
            <div className="space-y-2">
              {items.map((tk) => (
                <div key={tk.id} data-testid={`ops-task-${tk.id}`} className="bg-white border border-navy/10 rounded-lg p-3 group">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm">{tk.title}</span>
                    <button onClick={() => del(tk)} className="text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {tk.priority && <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full ${PRIO[tk.priority]}`}>{tk.priority}</span>}
                  <select data-testid={`ops-task-assign-${tk.id}`} value={tk.assignee_id || ""} onChange={(e) => upd(tk, { assignee_id: e.target.value })} className="mt-2 w-full text-[11px] bg-cream border border-navy/10 rounded px-1.5 py-1 outline-none">
                    <option value="">{t("os.ops.noAssignee")}</option>
                    {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <select data-testid={`ops-task-move-${tk.id}`} value={tk.status} onChange={(e) => upd(tk, { status: e.target.value })} className="mt-1.5 w-full text-[11px] bg-cream border border-navy/10 rounded px-1.5 py-1 outline-none">
                    {COL_KEYS.map((v) => <option key={v} value={v}>{t(`os.ops.cols.${v}`)}</option>)}
                  </select>
                  <div className="mt-1.5 grid grid-cols-2 gap-1">
                    <label className="text-[9px] text-navy/40 uppercase tracking-wide">{t("os.ops.start")}
                      <input data-testid={`ops-task-start-${tk.id}`} type="date" value={tk.start_date || ""} onChange={(e) => upd(tk, { start_date: e.target.value })} className="w-full text-[11px] bg-cream border border-navy/10 rounded px-1 py-0.5 outline-none" /></label>
                    <label className="text-[9px] text-navy/40 uppercase tracking-wide">{t("os.ops.due")}
                      <input data-testid={`ops-task-due-${tk.id}`} type="date" value={tk.due_date || ""} onChange={(e) => upd(tk, { due_date: e.target.value })} className="w-full text-[11px] bg-cream border border-navy/10 rounded px-1 py-0.5 outline-none" /></label>
                  </div>
                </div>
              ))}
              {adding === st ? (
                <div className="space-y-1.5">
                  <input autoFocus data-testid={`ops-board-input-${st}`} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add(st)} placeholder={t("os.ops.titlePlaceholder")} className="w-full text-sm bg-white border border-navy/10 rounded px-2 py-1.5 outline-none focus:border-violet" />
                  <div className="flex gap-1"><button onClick={() => add(st)} className="text-xs bg-violet text-white rounded px-2 py-1">{t("os.ops.add")}</button><button onClick={() => { setAdding(""); setTitle(""); }} className="text-xs text-navy/40 px-2">{t("os.ops.cancel")}</button></div>
                </div>
              ) : (
                <button data-testid={`ops-board-add-${st}`} onClick={() => { setAdding(st); setTitle(""); }} className="w-full text-xs text-navy/40 hover:text-violet flex items-center gap-1 py-1.5 transition-colors"><Plus className="w-3.5 h-3.5" /> {t("os.ops.add")}</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Timeline (cronograma) ---------------- */
function Timeline({ data }) {
  const { t } = useI18n();
  const items = [...data.tasks].filter((tk) => tk.due_date || tk.start_date)
    .sort((a, b) => (a.due_date || a.start_date || "9999").localeCompare(b.due_date || b.start_date || "9999"));
  if (items.length === 0) return <div className="text-navy/40 py-10 text-center border border-dashed border-navy/15 rounded-lg">{t("os.ops.timelineEmpty")}</div>;
  return (
    <div className="space-y-2">
      {items.map((tk) => (
        <div key={tk.id} className="flex items-center gap-4 bg-white border border-navy/10 rounded-lg px-5 py-3" data-testid={`ops-timeline-${tk.id}`}>
          <CalendarRange className="w-4 h-4 text-violet shrink-0" />
          <div className="flex-1 text-sm font-medium">{tk.title}</div>
          <div className="text-xs text-navy/50">{tk.start_date || "—"} → {tk.due_date || "—"}</div>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-navy/10 text-navy/60">{t(`os.ops.cols.${tk.status}`)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Quote (cotización) ---------------- */
function Quote({ id, p, onSaved }) {
  const { t } = useI18n();
  const q = p.quote || {};
  const [items, setItems] = useState(q.items || []);
  const [margin, setMargin] = useState(q.margin_pct || 0);
  const [tax, setTax] = useState(q.tax_pct || 0);
  const addItem = () => setItems([...items, { id: String(Date.now()), description: "", qty: 1, unit_price: 0 }]);
  const upd = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems(items.filter((_, idx) => idx !== i));
  const subtotal = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const total = (subtotal + subtotal * margin / 100) * (1 + tax / 100);
  const save = async () => {
    try { await api.put(`/operations/projects/${id}/quote`, { items, margin_pct: Number(margin), tax_pct: Number(tax) }); toast.success(t("os.ops.quoteSaved")); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.ops.err")); }
  };
  return (
    <div className="bg-white border border-navy/10 rounded-lg p-6 space-y-3">
      <p className="text-sm text-navy/50">{t("os.ops.quoteHint")}</p>
      {items.map((it, i) => (
        <div key={it.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`ops-quote-item-${i}`}>
          <input placeholder={t("os.ops.concept")} value={it.description} onChange={(e) => upd(i, "description", e.target.value)} className={`${input} col-span-6`} />
          <input type="number" placeholder={t("os.ops.qty")} value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className={`${input} col-span-2`} />
          <input type="number" placeholder={t("os.ops.price")} value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className={`${input} col-span-3`} />
          <button onClick={() => rm(i)} className="col-span-1 text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button data-testid="ops-quote-add-item" onClick={addItem} className="text-sm text-violet hover:underline flex items-center gap-1"><Plus className="w-4 h-4" /> {t("os.ops.addConcept")}</button>
      <div className="flex flex-wrap gap-4 pt-3 border-t border-navy/10 items-end">
        <div><label className="text-xs text-navy/50 block">{t("os.ops.margin")}</label><input type="number" value={margin} onChange={(e) => setMargin(e.target.value)} className={`${input} w-24`} /></div>
        <div><label className="text-xs text-navy/50 block">{t("os.ops.tax")}</label><input type="number" value={tax} onChange={(e) => setTax(e.target.value)} className={`${input} w-24`} /></div>
        <div className="ml-auto text-right"><div className="text-xs text-navy/50">{t("os.ops.total")}</div><div className="text-2xl font-medium" data-testid="ops-quote-total">{money(total)}</div></div>
      </div>
      <button data-testid="ops-quote-save" onClick={save} className="rounded-full bg-gradient-violet text-white px-6 py-2.5 text-sm hover:opacity-90 transition-opacity">{t("os.ops.saveQuote")}</button>
    </div>
  );
}

/* ---------------- Finance ---------------- */
function Finance({ id, data, onChange }) {
  const { t } = useI18n();
  const [f, setF] = useState({ type: "expense", concept: "", amount: "", supplier: "", date: "", status: "pending" });
  const [imp, setImp] = useState(false);
  const impRef = useRef();
  const add = async () => {
    if (!f.concept.trim()) { toast.error(t("os.ops.writeConcept")); return; }
    try { await api.post(`/operations/projects/${id}/expenses`, { ...f, amount: Number(f.amount || 0) });
      setF({ type: "expense", concept: "", amount: "", supplier: "", date: "", status: "pending" }); toast.success(f.type === "supplier_quote" ? t("os.ops.quoteRecorded") : t("os.ops.expenseAdded")); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.ops.err")); }
  };
  const del = async (e) => { try { await api.delete(`/operations/expenses/${e.id}`); onChange(); } catch (er) { toast.error(t("os.ops.err")); } };
  const togglePaid = async (e) => { try { await api.patch(`/operations/expenses/${e.id}`, { status: e.status === "paid" ? "pending" : "paid" }); onChange(); } catch (er) { toast.error(t("os.ops.err")); } };
  const convertToExpense = async (e) => { try { await api.patch(`/operations/expenses/${e.id}`, { type: "supplier_payment" }); toast.success(t("os.ops.convertedToExpense")); onChange(); } catch (er) { toast.error(t("os.ops.err")); } };
  const importQuote = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return; setImp(true);
    const fd = new FormData(); fd.append("file", file); fd.append("type", "supplier_quote");
    try { await api.post(`/operations/projects/${id}/import`, fd); toast.success(t("os.ops.quoteImported")); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.ops.cannotReadQuote")); }
    finally { setImp(false); if (impRef.current) impRef.current.value = ""; }
  };
  const tot = data.totals;
  const supplierQuotes = (data.expenses || []).filter((e) => e.type === "supplier_quote");
  const minQuote = supplierQuotes.length ? Math.min(...supplierQuotes.map((e) => Number(e.amount || 0))) : 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-navy/10 rounded-lg p-4"><div className="text-xs text-navy/40">{t("os.ops.expenseTotal")}</div><div className="text-xl font-medium text-rose-600">{money(tot.expenses)}</div></div>
        <div className="bg-white border border-navy/10 rounded-lg p-4"><div className="text-xs text-navy/40">{t("os.ops.paid")}</div><div className="text-xl font-medium text-emerald-600">{money(tot.paid)}</div></div>
        <div className="bg-white border border-navy/10 rounded-lg p-4"><div className="text-xs text-navy/40">{t("os.ops.pending")}</div><div className="text-xl font-medium text-gold">{money(tot.pending)}</div></div>
      </div>
      <div className="bg-white border border-navy/10 rounded-lg p-4 grid md:grid-cols-6 gap-2 items-end">
        <select data-testid="ops-fin-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={input}>{FIN_KEYS.map((v) => <option key={v} value={v}>{t(`os.ops.finTypes.${v}`)}</option>)}</select>
        <input data-testid="ops-fin-concept" placeholder={t("os.ops.concept")} value={f.concept} onChange={(e) => setF({ ...f, concept: e.target.value })} className={`${input} md:col-span-2`} />
        <input data-testid="ops-fin-amount" type="number" placeholder={t("os.ops.amount")} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={input} />
        <input data-testid="ops-fin-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={input} />
        <button data-testid="ops-fin-add" onClick={add} className="rounded-full bg-gradient-violet text-white text-sm py-2.5 hover:opacity-90 transition-opacity">{t("os.ops.add")}</button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="ops-fin-supplier" placeholder={t("os.ops.supplierPlaceholder")} value={f.supplier} onChange={(e) => setF({ ...f, supplier: e.target.value })} className={`${input} md:w-1/3`} />
        <input ref={impRef} type="file" accept=".pdf" className="hidden" data-testid="ops-fin-import-input" onChange={importQuote} />
        <button data-testid="ops-fin-import" onClick={() => impRef.current?.click()} disabled={imp} className="inline-flex items-center gap-1.5 rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50">
          {imp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {t("os.ops.importQuote")}
        </button>
        {tot.supplier_quotes > 0 && (
          <span className="text-xs text-navy/55 inline-flex items-center gap-1.5" data-testid="ops-fin-supplier-quotes-total">
            <DollarSign className="w-3.5 h-3.5 text-navy/40" /> {t("os.ops.supplierQuotesTotal")}: <span className="font-medium text-navy/80">{money(tot.supplier_quotes)}</span>
          </span>
        )}
      </div>
      {supplierQuotes.length >= 2 && (
        <div className="bg-white border border-navy/10 rounded-lg p-4" data-testid="ops-fin-supplier-compare">
          <div className="flex items-center gap-2 mb-3">
            <GitCompare className="w-4 h-4 text-violet" />
            <span className="text-sm font-medium">{t("os.ops.compareSuppliers")}</span>
            <span className="text-[11px] text-navy/40">· {supplierQuotes.length}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...supplierQuotes].sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0)).map((e) => {
              const best = Number(e.amount || 0) === minQuote;
              return (
                <div key={e.id} data-testid={`ops-fin-compare-${e.id}`}
                  className={`rounded-lg border p-3 flex flex-col gap-1.5 ${best ? "border-emerald-500 bg-emerald-500/[0.04]" : "border-navy/10"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{e.supplier || "Proveedor"}</span>
                    {best && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 shrink-0">{t("os.ops.mostConvenient")}</span>}
                  </div>
                  <div className="text-xs text-navy/50 truncate">{e.concept || "—"}</div>
                  <div className="text-lg font-medium mt-auto">{money(e.amount)}</div>
                  <button data-testid={`ops-fin-compare-convert-${e.id}`} onClick={() => convertToExpense(e)}
                    className="mt-1 inline-flex items-center justify-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-emerald-500 hover:text-emerald-600 transition-colors">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {t("os.ops.chooseConvert")}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="space-y-2">
        {data.expenses.length === 0 && <div className="text-navy/40 text-sm py-4">{t("os.ops.noExpenses")}</div>}
        {data.expenses.map((e) => (
          <div key={e.id} data-testid={`ops-fin-entry-${e.id}`} className="flex items-center gap-3 bg-white border border-navy/10 rounded-lg px-4 py-2.5 group">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-navy/10 text-navy/60">{t(`os.ops.finTypes.${e.type}`)}</span>
            <span className="text-sm flex-1 truncate">{e.concept}{e.supplier ? ` · ${e.supplier}` : ""}</span>
            <span className="text-xs text-navy/40">{e.date}</span>
            {(e.attachments || []).map((a) => (
              <button key={a.id} data-testid={`ops-fin-att-${a.id}`} onClick={() => downloadBlob(`/operations/expenses/${e.id}/attachment/${a.id}`, a.filename, t("os.ops.cannotDownload"))} className="text-navy/40 hover:text-violet"><Download className="w-4 h-4" /></button>
            ))}
            {e.type === "supplier_quote" ? (
              <button data-testid={`ops-fin-convert-${e.id}`} onClick={() => convertToExpense(e)}
                className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> {t("os.ops.convertToExpense")}
              </button>
            ) : (
              <button data-testid={`ops-fin-paid-${e.id}`} onClick={() => togglePaid(e)}
                className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${e.status === "paid" ? "bg-emerald-500/10 text-emerald-600" : "bg-gold/15 text-gold hover:bg-gold/25"}`}>
                {e.status === "paid" ? t("os.ops.paid") : t("os.ops.toPay")}
              </button>
            )}
            <span className="text-sm font-medium w-24 text-right">{money(e.amount)}</span>
            <button onClick={() => del(e)} className="text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ---------------- Comments (with attachments) ---------------- */
function Comments({ id }) {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const load = useCallback(() => { api.get(`/operations/projects/${id}/comments`).then((r) => setList(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!text.trim() && !file) return; setBusy(true);
    try {
      if (file) { const fd = new FormData(); fd.append("file", file); fd.append("body", text); await api.post(`/operations/projects/${id}/comments/upload`, fd); }
      else { await api.post(`/operations/projects/${id}/comments`, { body: text }); }
      setText(""); setFile(null); if (ref.current) ref.current.value = ""; load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("os.ops.err")); } finally { setBusy(false); }
  };
  const del = async (c) => { try { await api.delete(`/operations/comments/${c.id}`); load(); } catch (e) { toast.error(t("os.ops.err")); } };
  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {list.map((c) => (
          <div key={c.id} className="bg-white border border-navy/10 rounded-lg p-4 group" data-testid={`ops-comment-${c.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-violet/15 text-violet text-[11px] flex items-center justify-center font-medium">{(c.author_name || "?").slice(0, 1).toUpperCase()}</span>
              <span className="text-sm font-medium">{c.author_name}</span>
              <span className="text-[11px] text-navy/35">{(c.created_at || "").slice(0, 10)}</span>
              <button onClick={() => del(c)} className="ml-auto text-navy/20 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {c.body && <p className="text-sm text-navy/75 whitespace-pre-wrap">{c.body}</p>}
            {(c.attachments || []).map((a) => (
              <button key={a.id} data-testid={`ops-comment-att-${a.id}`} onClick={() => downloadBlob(`/operations/comments/${c.id}/attachment/${a.id}`, a.filename, t("os.ops.cannotDownload"))}
                className="mt-2 inline-flex items-center gap-2 text-sm text-violet bg-violet/5 rounded-lg px-3 py-2 hover:bg-violet/10 transition-colors">
                <Paperclip className="w-3.5 h-3.5" /> {a.filename} <Download className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
        ))}
        {list.length === 0 && <div className="text-navy/40 text-sm py-6 text-center">{t("os.ops.firstComment")}</div>}
      </div>
      <div className="bg-white border border-navy/10 rounded-lg p-3 space-y-2">
        <textarea data-testid="ops-comment-input" rows={2} placeholder={t("os.ops.messagePlaceholder")} value={text} onChange={(e) => setText(e.target.value)} className={input} />
        <div className="flex items-center gap-2">
          <input ref={ref} type="file" data-testid="ops-comment-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
          <button onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 text-sm text-navy/60 hover:text-violet border border-navy/15 rounded-full px-3 py-2 transition-colors"><Paperclip className="w-4 h-4" /> {file ? file.name.slice(0, 18) : t("os.ops.attach")}</button>
          <button data-testid="ops-comment-send" onClick={send} disabled={busy} className="ml-auto inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white px-5 py-2 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("os.ops.send")}</button>
        </div>
      </div>
    </div>
  );
}
